import { useNinetailed } from '@ninetailed/experience.js-next';
import * as authApi from 'api/auth';
import { getCheckoutSession, isIdentifiedCustomer } from 'api/checkout';
import {
  activateAccount,
  fetchCustomer as fetchCustomerDetails,
  passwordForgot,
  passwordReset,
} from 'api/customer';
import { AxiosResponse } from 'axios';
import { useConfig } from 'config/use-config';
import { useSentryContext } from 'contexts/sentry';
import cookie from 'cookie';
import { useHotjarSentryConnection } from 'hooks/sentry/useHotjarTracking';
import { Address } from 'interfaces/address';
import { Customer, CustomerSubscriptionStatus } from 'interfaces/customer';
import { referalTokenStore } from 'local-storage';
import { LoginResponse, isWrongShopError } from 'models/auth';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useSessionStorage } from 'react-use';
import { useCommonStore } from 'stores/common';
import { useCookieStore } from 'stores/cookie';
import { useSavingPlanStore } from 'stores/saving-plan';
import { Gtm } from 'tracking';
import { axios, isAxiosError } from 'utils/axios';
import { getIpAddress } from 'utils/get-ip-address';
import { initialSessionPromise, mapCheckoutSessionToGtmSession } from './initialSession';
import { useRootStore } from 'stores/use-root-store';

export const customerLocalStorageItemsCleanup = (): void => {
  referalTokenStore.remove();
};

interface AuthContextType {
  billingAddress: Address | null;
  /**
   * indicates if getSession is already called
   * */
  isSessionFetched: boolean;
  shippingAddress: Address | null;
  usingBillingAddress: boolean;
  authError: Error | undefined;
  customer?: Customer;
  isCustomerReady: boolean;
  isGuest: boolean;
  isUserAuthenticated: boolean;
  isUserLoggedIn: boolean;
  loading: boolean;
  referalToken?: string;
  fetchCustomer: () => Promise<void>;
  onActivateAccount: ({
    token,
    password,
  }: {
    token: string;
    password: string;
  }) => ReturnType<typeof activateAccount>;
  onLogin: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<LoginResponse['data']['customer']>;
  onOtpLogin: (email: string, otp: string) => Promise<{ id: number; email: string } | undefined>;
  onLogout: () => void;
  onRecoverAccount: (email: string) => Promise<{
    success: boolean;
    message?: string;
  }>;
  onResetPassword: (
    password: string,
    passwordConfirm: string,
    token: string
  ) => ReturnType<typeof activateAccount>;
  onSignUp: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<{ customerId: number; email: string }>;
  setAuthState: ({ referalToken, customer }: { referalToken: string; customer: Customer }) => void;
  setBillingAddress: (address: Address | null) => void;
  setCustomer: (customer: Customer | undefined) => void;
  setShippingAddress: (address: Address | null) => void;
  shouldUseBillingAddress: (useBilling: boolean) => void;
  clearAuthError: () => void;
}

/**
 * Work around to get hot fix done due to Context dependency issue
 */
const trackHotjarEvent = (
  eventName: string = 'start_hotjar',
  eventProperties?: Record<string, unknown>
): void => {
  try {
    // Check if Hotjar is available
    if (typeof window !== 'undefined' && typeof window.hj === 'function') {
      if (eventProperties) {
        window.hj('event', eventName, eventProperties);
      } else {
        window.hj('event', eventName);
      }
    }
  } catch (error) {
    console.error('Error tracking Hotjar event:', error);
  }
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { cookies, updateCookie } = useCookieStore();
  const { shopId } = useConfig();
  const { mergeUserData } = useSentryContext();
  const [referalToken, setReferalToken] = useState(referalTokenStore.get());
  const [authError, setAuthError] = useState<Error | undefined>();
  const [justCompletedOtpLogin, setJustCompletedOtpLogin] = useState(false);
  const { setAvailablePlans } = useSavingPlanStore();
  const setCustomerId = useRootStore((store) => store.setCustomerId);
  const setIsLoggedIn = useRootStore((store) => store.setIsLoggedIn);
  const switchWithMigration = useRootStore((store) => store.switchWithMigration);

  const [shippingAddress, setShippingAddress] = useSessionStorage<Address | null>(
    'pd:checkout-shipping',
    null
  );
  const [usingBillingAddress, shouldUseBillingAddress] = useSessionStorage<boolean>(
    'pd:checkout-use-billing',
    false
  );
  const [billingAddress, setBillingAddress] = useSessionStorage<Address | null>(
    'pd:checkout-billing-address',
    null
  );

  const { identify } = useNinetailed();

  const [customer, setCustomer] = useState<Customer | undefined>();
  const [loading, setLoading] = useState(true);

  const [isUserLoggedIn, setIsUserLoggedIn] = useState(!!Number(cookies?.isLoggedIn));

  useEffect(() => {
    const newLoggedInState = !!Number(cookies?.isLoggedIn);
    setIsUserLoggedIn(newLoggedInState);
  }, [cookies?.isLoggedIn]);

  /** Push shopId to identify shop */
  React.useEffect(() => {
    identify('', { shopId });
  }, [identify, shopId]);

  // Push created at timestamp and savings plan to Ninetailed with every shop visit
  React.useEffect(() => {
    if (customer && customer.createdAt) {
      const savingsPlanTrait = customer.savingsPlan
        ? `active-${customer.savingsPlan.planName}-${customer.savingsPlan.discountPct}`
        : 'none';

      const subscriptionStatus = customer.subscriptionStatus ?? CustomerSubscriptionStatus.None;

      identify(customer.customerId + '', {
        createdAt: customer.createdAt,
        savingsPlan: savingsPlanTrait,
        subscriptionStatus,
      });

      Gtm.session({
        customerSubscriptionStatus: subscriptionStatus,
        customerSavingsPlan: savingsPlanTrait,
      });
    }
  }, [customer, identify]);

  React.useEffect(() => {
    if (loading) {
      return;
    }

    if (!customer) {
      Gtm.session({
        customerEmail: undefined,
        customerId: undefined,
        customerOrdersCount: undefined,
      });

      return;
    }

    Gtm.session({
      customerEmail: customer.email,
      customerId: customer.customerId,
      customerOrdersCount: customer.ordersCount,
    });

    identify(customer.customerId + '', {
      isUserLoggedIn: true,
    });
  }, [customer, loading, identify]);

  /**
   * @TODO
   * This state should be indicated if customer is undefined or not.
   * But we differentiate EC or NC by this way.
   * Should be refactored
   */
  const [isSessionFetched, setIsSessionFetched] = useState(false);

  /** The client IPv6 address is fetched only once and stored in the common store */
  const { clientIpv6, isIpv6fetched, setClientIpv6, setIsIpv6fetched } = useCommonStore();

  useEffect(() => {
    if (!isIpv6fetched) {
      getIpAddress().then((ipAddress) => {
        setIsIpv6fetched(true);
        setClientIpv6(ipAddress);
      });
    }
  }, [isIpv6fetched, setClientIpv6, setIsIpv6fetched]);

  const fetchCustomer = useCallback(async () => {
    try {
      const initialSessionData = await initialSessionPromise;
      const data =
        isSessionFetched || !initialSessionData ? await getCheckoutSession() : initialSessionData;

      if (!data) {
        setLoading(false);
        return;
      }

      // initialSessionData is already tracked, so we skip tracking it again
      if (data !== initialSessionData) {
        Gtm.session(mapCheckoutSessionToGtmSession(data));
      }

      setIsSessionFetched(true);

      if (isIdentifiedCustomer(data)) {
        setCustomer({
          ...data,
          id: data.customerId,
        });

        // Cookie-rehydrated session (no interactive onLogin/onOtpLogin) must still set the store id, else savePetProfile stays on the guest branch.
        setCustomerId({ customerId: data.customerId });

        // Fetch addresses and set default shipping address
        // This is particularly important for OTP login where there is no customer data in the response
        try {
          if (!isUserLoggedIn) return;

          const details = await fetchCustomerDetails(data.customerId);
          if (details && details.addresses && details.addresses.length > 0) {
            const defaultAddress =
              details.addresses.find((addr) => addr.default) || details.addresses[0];
            if (defaultAddress) {
              setShippingAddress(defaultAddress);
            }
            if (details && details.savingsPlan) {
              setCustomer({
                ...data,
                id: data.customerId,
                savingsPlan: details.savingsPlan,
              });
            }
          }
        } catch (err) {
          console.error('Fetch Customer - Error:', err);
        }
      } else {
        setCustomer(undefined);
      }
    } catch (e) {
      console.error('📡 Fetch Customer - Error:', e, 'getCheckoutSession');
    } finally {
      setLoading(false);

      // Reset OTP flag after customer data is fetched
      if (justCompletedOtpLogin) {
        setJustCompletedOtpLogin(false);
      }
    }
  }, [isSessionFetched, isUserLoggedIn, justCompletedOtpLogin, setShippingAddress, setCustomerId]);

  React.useEffect(() => {
    // Should avoid calling the endpoint without the ref parameter
    if (typeof window !== 'undefined') {
      fetchCustomer();
    }
  }, [isUserLoggedIn, fetchCustomer]);

  // isUserAuthenticated is true when pd_ca is true OR otp_authenticated is true
  const [isUserAuthenticated, setIsUserAuthenticated] = useState<boolean>(
    cookies?.pd_ca === '1' || cookies?.otp_authenticated === '1'
  );

  // Keep isUserAuthenticated in sync with cookie changes
  React.useEffect(() => {
    setIsUserAuthenticated(cookies?.pd_ca === '1' || cookies?.otp_authenticated === '1');
  }, [cookies?.pd_ca, cookies?.otp_authenticated]);

  const isGuest = React.useMemo(() => {
    return Boolean(customer) && isUserLoggedIn === false && isUserAuthenticated;
  }, [customer, isUserLoggedIn, isUserAuthenticated]);

  React.useEffect(() => {
    if (!isUserLoggedIn) {
      setCustomer(undefined);
    }
  }, [isUserLoggedIn]);

  /** Should be set true if user is logged in as a registered customer*/
  const setLoggedIn = React.useCallback(
    (login: boolean): void => {
      const date = new Date();
      date.setDate(date.getDate() + 90);
      updateCookie('isLoggedIn', `${login ? 1 : 0}`, {
        expires: date,
        path: '/',
      });
    },
    [updateCookie]
  );

  const authInterceptor = React.useCallback(
    (res: AxiosResponse): unknown => {
      const currentCookies = cookie.parse(document.cookie);

      if (currentCookies.pd_ca) {
        setIsUserAuthenticated(currentCookies.pd_ca === '1');
      } else {
        // Check for OTP authentication cookie as alternative
        const otpAuthCookie = currentCookies.otp_authenticated;

        // Don't reset auth state if we just completed OTP login or have OTP auth cookie
        if (!justCompletedOtpLogin && !otpAuthCookie) {
          setIsUserAuthenticated(false);
          setLoggedIn(false);
        } else {
          // If we have OTP authentication, set user as authenticated
          if (otpAuthCookie) {
            setIsUserAuthenticated(true);
            setLoggedIn(true);
          }
        }
      }
      return res;
    },
    [justCompletedOtpLogin, setLoggedIn, setIsUserAuthenticated]
  );

  React.useEffect(() => {
    const interceptor = axios.interceptors.response.use(authInterceptor);

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [authInterceptor]);

  const petsdeliSignUp = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      const data = await authApi.register({ email, password });

      if (data.error) {
        throw Error(data?.error.type);
      }

      setReferalToken(data.data?.referalToken);
      referalTokenStore.set(data.data?.referalToken as string);

      return {
        customerId: data.data?.customer.id,
        email: data.data?.customer.email,
      };
    },
    []
  );

  /** Set state required after authentication */
  const setAuthState: AuthContextType['setAuthState'] = useCallback(
    ({ referalToken, customer }) => {
      setReferalToken(referalToken);
      referalTokenStore.set(referalToken);

      const _customer: Customer = {
        email: customer.email,
        customerId: customer.id,
        id: customer.id,
        ordersCount: customer.ordersCount,
        createdAt: customer.createdAt,
      };

      setCustomer(_customer);
      setLoggedIn(true);
      setIsUserLoggedIn(true);
      setIsUserAuthenticated(true);
    },
    [setLoggedIn]
  );

  const onLogin: AuthContextType['onLogin'] = useCallback(
    async ({ email, password }) => {
      try {
        const response = await authApi.login({ email, password });

        const { customer, referalToken } = response.data.data;

        setIsUserAuthenticated(true);

        // Assign address if user login while in checkout flow.
        if (customer.defaultAddress) {
          setShippingAddress({
            id: customer.defaultAddress.id,
            firstName: customer.defaultAddress.firstName,
            lastName: customer.defaultAddress.lastName,
            address1: customer.defaultAddress.address1,
            company: customer.defaultAddress.company,
            city: customer.defaultAddress.city,
            zip: customer.defaultAddress.zip,
            countryCode: customer.defaultAddress.countryCode,
            phone: customer.defaultAddress.phone,
            default: customer.defaultAddress.default,
          });
        }

        setAuthState({
          customer: {
            ...customer,
            customerId: customer.id,
          },
          referalToken,
        });

        setCustomerId({ customerId: customer.id });

        return customer;
      } catch (error) {
        setAuthError(error);

        if (isAxiosError<LoginResponse>(error)) {
          if (error.response?.status === 403) {
            trackHotjarEvent('login_error_403');
          }

          if (isWrongShopError(error)) {
            throw error;
          }

          throw error.response?.data.error;
        }

        throw error;
      }
    },
    [setAuthState, setShippingAddress, setCustomerId]
  );

  const onOtpLogin: AuthContextType['onOtpLogin'] = useCallback(
    async (email: string, otp: string) => {
      try {
        // Set flag to prevent interceptor from resetting auth state
        setJustCompletedOtpLogin(true);

        const result = await authApi.validateOtp(email, otp);

        if (!result.success) {
          // Handle OTP validation errors
          let errorMessage: string;
          switch (result.error) {
            case 'invalidCode':
              errorMessage = 'validation:otp-invalid-code';
              break;
            case 'tooManyAttempts':
              errorMessage = 'validation:otp-too-many-attempts';
              break;
            default:
              errorMessage = 'validation:otp-unknown-error';
          }

          const error = new Error(errorMessage);
          setAuthError(error);
          setJustCompletedOtpLogin(false);
          return undefined;
        }

        if (result.customer) {
          setIsUserAuthenticated(true);

          // Convert OTP customer response to expected format
          const customer = {
            id: Number(result.customer.id),
            email: result.customer.email,
            ordersCount: 0, // Will be updated by fetchCustomer
          };

          setAuthState({
            customer: {
              ...customer,
              customerId: Number(result.customer.id),
            },
            referalToken: '', // OTP doesn't provide referral token initially
          });

          // Set persistent cookie to maintain OTP authentication across page refreshes
          const expiryDate = new Date();
          expiryDate.setDate(expiryDate.getDate() + 270);
          updateCookie('otp_authenticated', '1', {
            expires: expiryDate,
            path: '/',
          });

          setCustomerId({ customerId: customer.id });
          return { id: customer.id, email: customer.email };
        }
      } catch (error) {
        console.error('❌ OTP Login failed:', error);
        setJustCompletedOtpLogin(false);
        setAuthError(error);
        throw error;
      }
    },
    [setAuthState, updateCookie, setJustCompletedOtpLogin, setCustomerId]
  );

  const onSignUp: AuthContextType['onSignUp'] = useCallback(
    async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }): Promise<{ customerId: number; email: string }> => {
      try {
        const res = await petsdeliSignUp({ email, password });

        setCustomer({
          id: res.customerId,
          customerId: res.customerId,
          email: res.email,
          ordersCount: 0,
          createdAt: Math.floor(Date.now() / 1000),
        });

        // Send data to ninetailed
        identify(res.customerId + '', {
          createdAt: Math.floor(Date.now() / 1000),
          ordersCount: 0,
          isUserLoggedIn: true,
        });

        // migrate local pet profiles to BE and updates the store
        await switchWithMigration(res.customerId);

        setLoggedIn(true);
        return res;
      } catch (error) {
        setAuthError(error);

        if (isAxiosError<LoginResponse>(error)) {
          if (isWrongShopError(error)) {
            throw error;
          }

          throw error.response?.data.error;
        }

        throw error;
      }
    },
    [petsdeliSignUp, identify, switchWithMigration, setLoggedIn]
  );

  const onLogout = useCallback(async () => {
    await authApi.logout();
    setReferalToken(undefined);
    setShippingAddress(null);
    if (customer) {
      identify(customer.customerId + '', {
        isUserLoggedIn: false,
      });
    }
    setBillingAddress(null);
    shouldUseBillingAddress(false);
    customerLocalStorageItemsCleanup();

    // Clear OTP authentication cookie and flag
    updateCookie('otp_authenticated', '', {
      expires: new Date(0),
      path: '/',
    });
    setJustCompletedOtpLogin(false);
    setAvailablePlans([]);

    setCustomer(undefined);
    setLoggedIn(false);
    setIsLoggedIn(false);
  }, [
    setShippingAddress,
    customer,
    setBillingAddress,
    shouldUseBillingAddress,
    updateCookie,
    setAvailablePlans,
    setLoggedIn,
    setIsLoggedIn,
    identify,
  ]);

  /**
   * Used with the PDex API to send a password reset email to the customer.
   */
  const onRecoverAccount = useCallback(
    async (email: string) => {
      try {
        const result = await passwordForgot(email);
        if (!result.success) {
          setAuthError(new Error(result.message));
        }
        return result;
      } catch (error) {
        setAuthError(error);
        return {
          success: false,
          message: 'An unexpected error occurred',
        };
      }
    },
    [setAuthError]
  );

  /**
   * Used with the PDex API to reset the customer's password.
   */
  const onResetPassword = useCallback(
    async (password: string, passwordConfirm: string, token: string) => {
      try {
        return await passwordReset(password, passwordConfirm, token);
      } catch (error) {
        setAuthError(error);
        throw error;
      }
    },
    [setAuthError]
  );

  /**
   * Pass the full URL from the activation email and the new password
   */
  const onActivateAccount = useCallback(
    async ({ token, password }: { token: string; password: string }) => {
      const res = await activateAccount(password, password, token);

      setCustomer({
        id: res.customer.id,
        customerId: res.customer.id,
        email: res.customer.email,
        ordersCount: 0,
        createdAt: Math.floor(Date.now() / 1000),
      });

      // Send data to ninetailed
      identify(res.customer.id + '', {
        createdAt: Math.floor(Date.now() / 1000),
        ordersCount: 0,
        isUserLoggedIn: true,
      });

      setLoggedIn(true);

      return res;
    },
    [identify, setLoggedIn]
  );

  const clearAuthError = useCallback(() => {
    setAuthError(undefined);
  }, []);

  // Connect Hotjar IDs to Sentry
  useHotjarSentryConnection(customer?.customerId, clientIpv6);

  /**
   * Set up basic user context for Sentry logs
   */
  React.useEffect(() => {
    if (!loading && customer) {
      mergeUserData({
        email: customer.email,
        id: customer.customerId,
      });
    } else if (!loading) {
      mergeUserData({});
    }
  }, [customer, loading, mergeUserData]);

  /**
   * Push release version to Ninetailed with every shop visit
   */
  useEffect(() => {
    if (cookies.pd_rv) {
      identify(`${customer ? customer.customerId : ''}`, {
        releaseVersion: cookies.pd_rv,
      });
    }
  }, [cookies.pd_rv, customer, identify]);

  return (
    <AuthContext.Provider
      value={{
        authError,
        billingAddress,
        customer,
        isCustomerReady: !loading && (!isUserLoggedIn || Boolean(customer)),
        isGuest,
        isSessionFetched,
        isUserAuthenticated,
        isUserLoggedIn,
        loading,
        referalToken,
        shippingAddress,
        usingBillingAddress,
        fetchCustomer,
        onLogin,
        onOtpLogin,
        onLogout,
        onRecoverAccount,
        onResetPassword,
        onSignUp,
        setAuthState,
        setBillingAddress,
        setCustomer,
        setShippingAddress,
        shouldUseBillingAddress,
        onActivateAccount,
        clearAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};
